import React, { useState, useEffect } from 'react';
import axios from '../../config/axios';
import CourseCard from '../CourseCard/CourseCard';

// دالة لتنسيق التاريخ بشكل صحيح
const formatDate = (dateString) => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    
    // تنسيق التاريخ بالعربية
    const day = date.getDate();
    const monthNames = [
      'يناير', 'فبراير', 'مارس', 'إبريل', 'مايو', 'يونيو',
      'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'
    ];
    const month = monthNames[date.getMonth()];
    
    return `${day} ${month}`;
  } catch (err) {
    // خطأ في تنسيق التاريخ
    return '';
  }
};

const PublicCoursesList = ({ categoryId }) => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        setLoading(true);
        const params = {};
        
        // إضافة فلتر التصنيف إذا كان موجوداً
        if (categoryId) {
          params.category_id = categoryId;
        }
        
        const response = await axios.get('/api/courses', { params });
        setCourses(response.data.data || []);
      } catch (err) {
        console.error('خطأ في جلب الدورات:', err);
        setError('حدث خطأ أثناء تحميل الدورات');
      } finally {
        setLoading(false);
      }
    };

    fetchCourses();
  }, [categoryId]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {[1, 2, 3].map((item) => (
          <div key={item} className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden animate-pulse h-[490px]">
            <div className="h-48 bg-gray-300 dark:bg-gray-700"></div>
            <div className="p-4">
              <div className="h-6 bg-gray-300 dark:bg-gray-700 rounded w-3/4 mb-3"></div>
              <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded w-1/2 mb-2"></div>
              <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
              <div className="flex justify-between items-center mt-4">
                <div className="h-8 bg-gray-300 dark:bg-gray-700 rounded w-1/4"></div>
                <div className="h-10 bg-gray-300 dark:bg-gray-700 rounded w-1/3"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-red-500">
        <p>{error}</p>
      </div>
    );
  }

  if (courses.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        <p>لا توجد دورات متاحة حالياً</p>
        <p className="text-gray-500 dark:text-gray-400 mt-2">يرجى التحقق لاحقاً أو تغيير معايير البحث</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {courses.map((course) => {
        // تنسيق التواريخ
        const formattedStartDate = formatDate(course.start_date);
        const formattedEndDate = formatDate(course.end_date);
        
        // تنسيق الأسعار
        let originalPrice = 'مجاني';
        let discountedPrice = 'مجاني';
        let discount = null;
        
        if (course.price && parseFloat(course.price) > 0) {
          // إذا كان هناك سعر أصلي
          // تحويل السعر لرقم صحيح بدون كسور عشرية
          const priceInt = parseInt(course.price);
          originalPrice = `${priceInt} ريال`;
          discountedPrice = originalPrice; // السعر المعروض هو نفس السعر الأصلي إذا لم يكن هناك خصم
          
          // إذا كان هناك سعر بعد الخصم
          if (course.discounted_price && parseFloat(course.discounted_price) > 0 && parseFloat(course.discounted_price) < parseFloat(course.price)) {
            // تحويل السعر المخفض لرقم صحيح بدون كسور عشرية
            const discountedPriceInt = parseInt(course.discounted_price);
            discountedPrice = `${discountedPriceInt} ريال`;
            
            // حساب نسبة الخصم إذا لم تكن موجودة
            if (!course.discount && course.price && course.discounted_price) {
              const originalValue = parseFloat(course.price);
              const discountedValue = parseFloat(course.discounted_price);
              if (originalValue > 0) {
                const discountPercent = Math.round(((originalValue - discountedValue) / originalValue) * 100);
                discount = `${discountPercent}%`;
              }
            } else if (course.discount) {
              discount = `${course.discount}%`;
            }
          }
        }
        
        // تنسيق مسارات الصور
        const coverImagePath = course.cover_image 
          ? (course.cover_image.startsWith('http') 
              ? course.cover_image 
              : course.cover_image) // مسار الصورة كامل من الباك إند
          : 'https://picsum.photos/id/1018/1920/1080';
          
        const coursePlanPath = course.plan_image
          ? (course.plan_image.startsWith('http')
              ? course.plan_image
              : course.plan_image) // مسار خطة الدورة كامل من الباك إند
          : '';
        
        return (
          <CourseCard 
            key={course.id}
            id={course.id}
            title={course.name}
            instructor={course.instructors?.[0]?.name || ''}
            category={course.category?.name || ''}
            originalPrice={originalPrice}
            discountedPrice={discountedPrice}
            discount={discount}
            startDate={formattedStartDate}
            endDate={formattedEndDate}
            coursePlan={coursePlanPath}
            coverImage={coverImagePath}
            isSubscribed={false}
            isExpired={course.is_expired || false}
            is_coming_soon_display={course.is_coming_soon_display || false}
          />
        );
      })}
    </div>
  );
};

export default PublicCoursesList;
